// Payment.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./Payment.css";
import { useLocalContext } from "../../context/LocalContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faShieldHalved,
  faCircleCheck,
  faCreditCard,
  faCircleInfo,
  faExclamationTriangle,
  faPhone,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import {
  faCcVisa,
  faCcMastercard,
  faCcAmex,
  faPaypal,
  faStripe,
  faApplePay,
  faGooglePay,
} from "@fortawesome/free-brands-svg-icons";

const Payment = () => {
  const { webinfo } = useLocalContext();
  const brand = webinfo?.name || "Our Store";

  const supportEmail = webinfo?.email || "support@example.com";
  const supportPhoneText = webinfo?.phone || "+91 80000 00000";
  const supportPhoneHref = webinfo?.phonecall
    ? `tel:${webinfo.phonecall}`
    : supportPhoneText
    ? `tel:${String(supportPhoneText).replace(/\s+/g, "")}`
    : "tel:+918000000000";

  const [method, setMethod] = useState("paypal");
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [card, setCard] = useState({
    name: "",
    number: "",
    expiry: "",
    cvv: "",
    brand: "",
  });

  const [billing, setBilling] = useState({
    fullname: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    email: "",
    phone: "",
  });

  const [touched, setTouched] = useState({});

  useEffect(() => {
    document.title = `Secure Checkout | ${brand}`;
  }, [brand]);

  // ---------- Helpers ----------
  const detectBrand = (val) => {
    const v = val.replace(/\D/g, "");
    if (/^4/.test(v)) return "Visa";
    if (/^5[1-5]/.test(v) || /^2(2[2-9]|[3-6]\d|7[01])/.test(v))
      return "Mastercard";
    if (/^3[47]/.test(v)) return "Amex";
    if (/^6/.test(v)) return "Discover";
    if (/^35/.test(v)) return "JCB";
    return "";
  };

  const luhn = (num) => {
    const s = num.replace(/\D/g, "");
    if (s.length < 12) return false;
    let sum = 0,
      dbl = false;
    for (let i = s.length - 1; i >= 0; i--) {
      let d = parseInt(s[i], 10);
      if (dbl) {
        d *= 2;
        if (d > 9) d -= 9;
      }
      sum += d;
      dbl = !dbl;
    }
    return sum % 10 === 0;
  };

  const formatCardNumber = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 19);
    return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  };

  const formatExpiry = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const validateExpiry = (mmYY) => {
    const m = mmYY.match(/^(\d{2})\/(\d{2})$/);
    if (!m) return false;
    const month = +m[1];
    const year = +m[2];
    if (month < 1 || month > 12) return false;
    const now = new Date();
    const yFull = 2000 + year;
    const lastDay = new Date(yFull, month, 0).getDate();
    const expDate = new Date(yFull, month - 1, lastDay, 23, 59, 59);
    return expDate >= now;
  };

  const maskedCvvLength = useMemo(
    () => (card.brand === "Amex" ? 4 : 3),
    [card.brand]
  );

  const emailOK = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  // ---------- Field handlers ----------
  const handleBilling = (e) => {
    const { name, value } = e.target;
    setBilling((s) => ({ ...s, [name]: value }));
  };

  const handleCard = (e) => {
    const { name, value } = e.target;
    if (name === "number") {
      const formatted = formatCardNumber(value);
      const brand = detectBrand(formatted);
      setCard((s) => ({ ...s, number: formatted, brand }));
      return;
    }
    if (name === "expiry") {
      setCard((s) => ({ ...s, expiry: formatExpiry(value) }));
      return;
    }
    if (name === "cvv") {
      const dig = value.replace(/\D/g, "").slice(0, maskedCvvLength);
      setCard((s) => ({ ...s, cvv: dig }));
      return;
    }
    setCard((s) => ({ ...s, [name]: value }));
  };

  const onBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
  };

  // ---------- Validation state ----------
  const billingErrors = useMemo(() => {
    const errs = {};
    if (!billing.fullname.trim()) errs.fullname = "Full name is required.";
    if (!billing.street.trim()) errs.street = "Street address is required.";
    if (!billing.city.trim()) errs.city = "City is required.";
    if (!billing.state.trim()) errs.state = "State/Region is required.";
    if (!billing.zip.trim()) errs.zip = "ZIP/Postal is required.";
    if (!emailOK(billing.email)) errs.email = "Enter a valid email.";
    if (!billing.phone.trim()) errs.phone = "Phone is required.";
    return errs;
  }, [billing]);

  const cardErrors = useMemo(() => {
    if (method !== "card") return {};
    const errs = {};
    if (!card.name.trim()) errs.name = "Name on card is required.";
    const raw = card.number.replace(/\s/g, "");
    if (!luhn(raw)) errs.number = "Enter a valid card number.";
    if (!validateExpiry(card.expiry))
      errs.expiry = "Enter a valid future date (MM/YY).";
    if (card.cvv.length !== maskedCvvLength)
      errs.cvv = `CVV must be ${maskedCvvLength} digits.`;
    return errs;
  }, [card, maskedCvvLength, method]);

  const canSubmit =
    Object.keys(billingErrors).length === 0 &&
    (method === "paypal" || Object.keys(cardErrors).length === 0) &&
    !processing;

  // ---------- Submit ----------
  const handleSubmit = (e) => {
    e.preventDefault();
    setProcessing(true);
    setErrorMsg("");

    // Simulated flow — replace with real gateway integration
    setTimeout(() => {
      setProcessing(false);

      // ✅ include BOTH email + phone in error message
      setErrorMsg(
        `We couldn’t finalize the payment. Please contact support at ${supportEmail} or call ${supportPhoneText} with your order details.`
      );
    }, 900);
  };

  // ---------- UI ----------
  const CardBrandIcon = () => {
    switch (card.brand) {
      case "Visa":
        return <FontAwesomeIcon icon={faCcVisa} />;
      case "Mastercard":
        return <FontAwesomeIcon icon={faCcMastercard} />;
      case "Amex":
        return <FontAwesomeIcon icon={faCcAmex} />;
      default:
        return <FontAwesomeIcon icon={faCreditCard} />;
    }
  };

  return (
    <div className="payment-page">
      {/* Hero */}
      <header className="pm-hero" role="banner">
        <div className="pm-hero__left">
          <h1 className="pm-title">
            <FontAwesomeIcon icon={faLock} /> Secure checkout
          </h1>
          <p className="pm-sub">
            Encrypted checkout + instant digital delivery from{" "}
            <strong>{brand}</strong>.
          </p>

          <div className="pm-trust">
            <span>
              <FontAwesomeIcon icon={faShieldHalved} /> SSL secured
            </span>
            <span>
              <FontAwesomeIcon icon={faCircleCheck} /> No card data stored
            </span>
            <span>
              <FontAwesomeIcon icon={faCircleInfo} /> Receipt on email
            </span>
          </div>

          {/* ✅ redesigned error card with links */}
          {errorMsg && (
            <div className="pm-alert pm-alert--error" role="alert">
              <div className="pm-alert__icon">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <div className="pm-alert__body">
                <div className="pm-alert__title">Payment not completed</div>
                <div className="pm-alert__text">{errorMsg}</div>
                <div className="pm-alert__actions">
                  <a className="pm-chip" href={`mailto:${supportEmail}`}>
                    <FontAwesomeIcon icon={faEnvelope} />
                    Email support
                  </a>
                  <a className="pm-chip" href={supportPhoneHref}>
                    <FontAwesomeIcon icon={faPhone} />
                    Call {supportPhoneText}
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="pm-hero__right" aria-hidden="true">
          <div className="pm-hero-card">
            <div className="pm-hero-card__label">Accepted methods</div>
            <div className="pm-hero-card__icons">
              <FontAwesomeIcon icon={faCcVisa} />
              <FontAwesomeIcon icon={faCcMastercard} />
              <FontAwesomeIcon icon={faCcAmex} />
              <FontAwesomeIcon icon={faPaypal} />
              <FontAwesomeIcon icon={faStripe} />
            </div>
            <div className="pm-hero-card__note">
              Powered by trusted payment processors.
            </div>
          </div>
        </div>
      </header>

      <main className="pm-grid">
        {/* Billing */}
        <section className="pm-panel" aria-labelledby="billing-title">
          <div className="pm-panel__head">
            <h2 id="billing-title" className="pm-h2">
              Contact & billing
            </h2>
            <span className="pm-pill">
              <FontAwesomeIcon icon={faCircleInfo} /> Used for invoice & delivery
            </span>
          </div>

          <form className="pm-form" onSubmit={(e) => e.preventDefault()}>
            <label className="pm-field">
              <span>Full name</span>
              <input
                name="fullname"
                value={billing.fullname}
                onChange={handleBilling}
                onBlur={onBlur}
                autoComplete="name"
                placeholder="Jane Doe"
                required
                aria-invalid={!!(touched.fullname && billingErrors.fullname)}
              />
              {touched.fullname && billingErrors.fullname && (
                <div className="pm-err">{billingErrors.fullname}</div>
              )}
            </label>

            <label className="pm-field">
              <span>Street address</span>
              <input
                name="street"
                value={billing.street}
                onChange={handleBilling}
                onBlur={onBlur}
                autoComplete="street-address"
                placeholder="123 Market Street"
                required
                aria-invalid={!!(touched.street && billingErrors.street)}
              />
              {touched.street && billingErrors.street && (
                <div className="pm-err">{billingErrors.street}</div>
              )}
            </label>

            <div className="pm-row pm-row--3">
              <label className="pm-field">
                <span>City</span>
                <input
                  name="city"
                  value={billing.city}
                  onChange={handleBilling}
                  onBlur={onBlur}
                  autoComplete="address-level2"
                  placeholder="San Francisco"
                  required
                  aria-invalid={!!(touched.city && billingErrors.city)}
                />
                {touched.city && billingErrors.city && (
                  <div className="pm-err">{billingErrors.city}</div>
                )}
              </label>

              <label className="pm-field">
                <span>State / Region</span>
                <input
                  name="state"
                  value={billing.state}
                  onChange={handleBilling}
                  onBlur={onBlur}
                  autoComplete="address-level1"
                  placeholder="CA"
                  required
                  aria-invalid={!!(touched.state && billingErrors.state)}
                />
                {touched.state && billingErrors.state && (
                  <div className="pm-err">{billingErrors.state}</div>
                )}
              </label>

              <label className="pm-field">
                <span>ZIP / Postal</span>
                <input
                  name="zip"
                  value={billing.zip}
                  onChange={handleBilling}
                  onBlur={onBlur}
                  autoComplete="postal-code"
                  placeholder="94105"
                  required
                  aria-invalid={!!(touched.zip && billingErrors.zip)}
                />
                {touched.zip && billingErrors.zip && (
                  <div className="pm-err">{billingErrors.zip}</div>
                )}
              </label>
            </div>

            <div className="pm-row pm-row--2">
              <label className="pm-field">
                <span>Email</span>
                <input
                  type="email"
                  name="email"
                  value={billing.email}
                  onChange={handleBilling}
                  onBlur={onBlur}
                  autoComplete="email"
                  placeholder="jane@example.com"
                  required
                  aria-invalid={!!(touched.email && billingErrors.email)}
                />
                {touched.email && billingErrors.email && (
                  <div className="pm-err">{billingErrors.email}</div>
                )}
              </label>

              <label className="pm-field">
                <span>Phone (for support)</span>
                <input
                  type="tel"
                  name="phone"
                  value={billing.phone}
                  onChange={handleBilling}
                  onBlur={onBlur}
                  autoComplete="tel"
                  placeholder="+91 98765 43210"
                  required
                  aria-invalid={!!(touched.phone && billingErrors.phone)}
                />
                {touched.phone && billingErrors.phone && (
                  <div className="pm-err">{billingErrors.phone}</div>
                )}
              </label>
            </div>

            <p className="pm-note">
              By placing this order you agree to our{" "}
              <a href="/terms-and-conditions">Terms</a>,{" "}
              <a href="/refund-policy">Refund Policy</a>, and{" "}
              <a href="/privacy-policy">Privacy Policy</a>.
            </p>
          </form>
        </section>

        {/* Payment */}
        <section className="pm-panel" aria-labelledby="payment-title">
          <div className="pm-panel__head">
            <h2 id="payment-title" className="pm-h2">
              Payment method
            </h2>
            <span className="pm-pill">
              <FontAwesomeIcon icon={faShieldHalved} /> Secured
            </span>
          </div>

          <div className="pm-tabs" role="tablist" aria-label="Payment method">
            <button
              role="tab"
              aria-selected={method === "paypal"}
              className={`pm-tab ${method === "paypal" ? "is-active" : ""}`}
              onClick={() => setMethod("paypal")}
              type="button"
            >
              <FontAwesomeIcon icon={faPaypal} /> Wallet
            </button>
            <button
              role="tab"
              aria-selected={method === "card"}
              className={`pm-tab ${method === "card" ? "is-active" : ""}`}
              onClick={() => setMethod("card")}
              type="button"
            >
              <FontAwesomeIcon icon={faCreditCard} /> Card
            </button>
          </div>

          <form className="pm-payform" onSubmit={handleSubmit} noValidate>
            {method === "paypal" && (
              <div className="pm-paybox">
                <p className="pm-help">
                  You’ll be redirected to PayPal (or available wallet) to confirm
                  payment, then returned with your receipt.
                </p>

                <div className="pm-wallets" aria-hidden="true">
                  <FontAwesomeIcon icon={faPaypal} title="PayPal" />
                  <FontAwesomeIcon icon={faApplePay} title="Apple Pay" />
                  <FontAwesomeIcon icon={faGooglePay} title="Google Pay" />
                </div>

                <button
                  type="submit"
                  className={`pm-pay ${processing ? "is-busy" : ""}`}
                  disabled={processing || Object.keys(billingErrors).length > 0}
                >
                  {processing ? (
                    "Processing…"
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaypal} />
                      Continue
                    </>
                  )}
                </button>

                {Object.keys(billingErrors).length > 0 && (
                  <p className="pm-inline-hint">
                    <FontAwesomeIcon icon={faExclamationTriangle} /> Complete the
                    billing form first.
                  </p>
                )}
              </div>
            )}

            {method === "card" && (
              <>
                <div className="pm-cardrow">
                  <div className="pm-cardbrand" aria-live="polite">
                    <CardBrandIcon />
                    <span>{card.brand || "Card"}</span>
                  </div>
                  <div className="pm-logos" aria-hidden="true">
                    <FontAwesomeIcon icon={faCcVisa} />
                    <FontAwesomeIcon icon={faCcMastercard} />
                    <FontAwesomeIcon icon={faCcAmex} />
                    <FontAwesomeIcon icon={faStripe} />
                  </div>
                </div>

                <label className="pm-field">
                  <span>Name on card</span>
                  <input
                    name="name"
                    value={card.name}
                    onChange={handleCard}
                    onBlur={onBlur}
                    placeholder="Jane Doe"
                    autoComplete="cc-name"
                    required
                    aria-invalid={!!(touched.name && cardErrors.name)}
                  />
                  {touched.name && cardErrors.name && (
                    <div className="pm-err">{cardErrors.name}</div>
                  )}
                </label>

                <label className="pm-field">
                  <span>Card number</span>
                  <input
                    name="number"
                    inputMode="numeric"
                    value={card.number}
                    onChange={handleCard}
                    onBlur={onBlur}
                    placeholder="1234 5678 9012 3456"
                    autoComplete="cc-number"
                    required
                    aria-invalid={!!(touched.number && cardErrors.number)}
                  />
                  {touched.number && cardErrors.number && (
                    <div className="pm-err">{cardErrors.number}</div>
                  )}
                </label>

                <div className="pm-row pm-row--2">
                  <label className="pm-field">
                    <span>Expiry (MM/YY)</span>
                    <input
                      name="expiry"
                      inputMode="numeric"
                      value={card.expiry}
                      onChange={handleCard}
                      onBlur={onBlur}
                      placeholder="MM/YY"
                      autoComplete="cc-exp"
                      required
                      aria-invalid={!!(touched.expiry && cardErrors.expiry)}
                    />
                    {touched.expiry && cardErrors.expiry && (
                      <div className="pm-err">{cardErrors.expiry}</div>
                    )}
                  </label>

                  <label className="pm-field">
                    <span>CVV</span>
                    <input
                      name="cvv"
                      inputMode="numeric"
                      value={card.cvv}
                      onChange={handleCard}
                      onBlur={onBlur}
                      placeholder={maskedCvvLength === 4 ? "4 digits" : "3 digits"}
                      autoComplete="cc-csc"
                      required
                      aria-invalid={!!(touched.cvv && cardErrors.cvv)}
                    />
                    {touched.cvv && cardErrors.cvv && (
                      <div className="pm-err">{cardErrors.cvv}</div>
                    )}
                  </label>
                </div>

                <button
                  type="submit"
                  className={`pm-pay ${processing ? "is-busy" : ""}`}
                  disabled={!canSubmit}
                >
                  {processing ? "Processing…" : "Pay now"}
                </button>

                <p className="pm-safe">
                  <FontAwesomeIcon icon={faLock} />
                  Encrypted transfer. We don’t store card details on {brand}.
                </p>
              </>
            )}
          </form>

          <div className="pm-badge">
            <FontAwesomeIcon icon={faShieldHalved} />
            <span>
              We use trusted payment processors and follow industry-standard
              security practices.
            </span>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Payment;
